import { useLocation, useNavigate } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import { useInitializationDataContext } from '../context/data-context';
import { AddressData } from '../global-types/addresses';
import { EnvelopeCode, EnvelopeType } from '../global-types/envelope';
import { deleteAddress } from '../services/address';
import { updateProject } from '../services/customization';
import { useAddressStore } from '../stores/address';

export type UseSkipAddressValidationParams = {
  envelope?: EnvelopeType | null;
};

export const useSkipAddressValidation = (params?: UseSkipAddressValidationParams) => {
  const { search } = useLocation();
  const navigate = useNavigate();
  const {
    initializedDataState: { data: initializedData },
  } = useInitializationDataContext();
  const { addressData, cleanAddresses } = useAddressStore(
    useShallow((state) => ({
      addressData: state.addressData,
      cleanAddresses: state.cleanAddresses,
    })),
  );

  const removeSavedAddresses = (projectId: string, addresses: AddressData[]) => {
    const addressIds = addresses.map((address) => address.address_id);
    for (let i = 0; i < addressIds.length; i++) {
      const addressId = addressIds[`${i}`];
      if (addressId) deleteAddress(projectId, addressId);
    }
  };

  const skipAddressValidation = async () => {
    const projectId = initializedData?.project_id;

    if (params?.envelope === EnvelopeType.Blank) {
      cleanAddresses();
      await updateProject(
        {
          envelope_type: EnvelopeCode.Blank,
        },
        projectId,
      );
    }

    // filling the submittedAddresses with the recipient and sender addresses
    const submittedAddresses = [addressData.recipient, addressData.sender].filter(
      (address): address is AddressData => address !== null,
    );

    if (submittedAddresses.length > 0) {
      // Delete address from backend
      removeSavedAddresses(initializedData?.project_id as string, submittedAddresses);
      // Remove address from state
      cleanAddresses();
    }
    // set envelope to blank by default if no envelope is passed
    const searchParams = new URLSearchParams(search);
    searchParams.set('envelope', params && params.envelope ? params.envelope : EnvelopeType.Blank);

    // navigate to preview view
    navigate({ pathname: '/card/customization/preview', search: searchParams.toString() });
  };

  return skipAddressValidation;
};
